import { systemOfMeasurementType } from 'Models/Enums';

const C = {
	// Length (NIST exact)
	feetToInches: 12,
	inchesToFeet: 1 / 12,
	millimetersToMeters: 0.001,

	// Mass (NIST exact)
	poundsToKilograms: 0.45359237,
} as const;

export const UnitLabels = {
	volume: {
		cubicMeters: 'm³',
		cubicFeet: 'ft³',
	},
	length: {
		millimeters: 'mm',
		inches: 'in',
		feet: 'ft',
		inchesShorthand: '"',
		feetShorthand: "'",
	},
	mass: {
		kilograms: 'kg',
		pounds: 'lb',
		tonne: 't',
		usTon: 'ton_us',
	},
};

export const DisplayUnits = {
	dimensions: {
		/**
		 * Imperial output: 0' 6" x 1' 0" x 0' 0"
		 * Metric output: 12 x 25 x 0
		 */
		format: (dimensions: Dimensions) => {
			let length = `${dimensions.length}`;
			let width = `${dimensions.width}`;
			let height = `${dimensions.height}`;

			if (dimensions.unitOfMeasurement === 'IMPERIAL') {
				length = DisplayUnits.length.imperialShorthand(UnitConverter.inchesToFeetAndInches(dimensions.length));
				width = DisplayUnits.length.imperialShorthand(UnitConverter.inchesToFeetAndInches(dimensions.width));
				height = DisplayUnits.length.imperialShorthand(UnitConverter.inchesToFeetAndInches(dimensions.height));
			}

			return `${length} x ${width} x ${height}`;
		},
	},
	length: {
		imperialShorthand: ({ feet, inches }: { feet: number; inches: number }) => {
			return `${feet}${UnitLabels.length.feetShorthand} ${inches}${UnitLabels.length.inchesShorthand}`;
		},
		format: (value: number, unitType: systemOfMeasurementType) => {
			if (unitType === 'METRIC') {
				return `${value}`;
			}
			const { feet, inches } = UnitConverter.inchesToFeetAndInches(value);
			return `${feet}${UnitLabels.length.feetShorthand} ${inches}${UnitLabels.length.inchesShorthand}`;
		},
	},
};

export interface Dimensions {
	length: number;
	width: number;
	height: number;
	unitOfMeasurement: systemOfMeasurementType;
}

export const UnitConverter = {
	getCubic: (dimensions: Dimensions) => {
		if (dimensions.unitOfMeasurement === 'IMPERIAL') {
			return UnitConverter.volumeFromInchesToCubicFeet(dimensions.length, dimensions.width, dimensions.height);
		}
		return UnitConverter.volumeFromMillimetersToCubicMeters(dimensions.length, dimensions.width, dimensions.height);
	},

	volumeFromMillimetersToCubicMeters: (lengthMm: number, widthMm: number, heightMm: number): number => {
		const l = lengthMm * C.millimetersToMeters;
		const w = widthMm * C.millimetersToMeters;
		const h = heightMm * C.millimetersToMeters;
		return l * w * h;
	},

	volumeFromInchesToCubicFeet: (lengthInches: number, widthInches: number, heightInches: number): number => {
		const l = lengthInches * C.inchesToFeet;
		const w = widthInches * C.inchesToFeet;
		const h = heightInches * C.inchesToFeet;
		return l * w * h;
	},

	inchesToFeet: (inches: number) => {
		return inches * C.inchesToFeet;
	},

	feetToInches: (feet: number) => {
		return feet * C.feetToInches;
	},

	inchesToFeetAndInches: (totalInches: number) => {
		const feet = Math.floor(UnitConverter.inchesToFeet(totalInches));
		const inches = totalInches - UnitConverter.feetToInches(feet);
		return { feet, inches };
	},
} as const;
